import { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { BreadcrumbListSchema, WebPageSchema } from '@/components/base/SchemaOrg';
import { allComics, categories } from '@/mocks/comics';

const ITEMS_PER_PAGE = 12;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState('全部');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (slug && slug !== 'all') {
      const found = categories.find((c) => c === slug || c === decodeURIComponent(slug));
      if (found) setActiveCategory(found);
    } else {
      setActiveCategory('全部');
    }
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const filteredComics = useMemo(() => {
    if (activeCategory === '全部') return allComics;
    return allComics.filter((c) => c.categories.includes(activeCategory));
  }, [activeCategory]);

  const totalPages = Math.ceil(filteredComics.length / ITEMS_PER_PAGE);
  const pagedComics = filteredComics.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const handleCategoryChange = (cat: string) => {
    setActiveCategory(cat);
    setCurrentPage(1);
    window.history.replaceState(null, '', cat === '全部' ? '/category/all' : `/category/${cat}`);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '分类浏览', to: '/category/all' },
    { label: activeCategory },
  ];

  return (
    <>
      <BreadcrumbListSchema
        items={[
          { name: '首页', item: 'https://www.kumanwu.com/' },
          { name: '分类浏览', item: 'https://www.kumanwu.com/category/all' },
          { name: activeCategory, item: `https://www.kumanwu.com/category/${activeCategory}` },
        ]}
      />
      <WebPageSchema
        title={`${activeCategory !== '全部' ? activeCategory + '漫画_' : ''}漫画分类_韩国漫画大全_酷漫屋`}
        description={`酷漫屋漫画分类页面，提供${activeCategory}类型的韩国漫画在线阅读，下拉式阅读体验，免费高清漫画尽在酷漫屋。`}
        url={`https://www.kumanwu.com/category/${activeCategory}`}
      />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb items={breadcrumbItems} />

          <section className="w-full py-10">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                {activeCategory === '全部' ? '漫画分类' : `${activeCategory}漫画`}
              </h1>
              <p className="text-sm text-foreground-500 mb-8">
                共找到 {filteredComics.length} 部漫画
              </p>

              {/* Category tabs */}
              <div className="flex items-center gap-2 mb-10 overflow-x-auto pb-2 flex-wrap">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => handleCategoryChange(cat)}
                    className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                      activeCategory === cat
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              {/* Comic grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {pagedComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>

              {filteredComics.length === 0 && (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                    <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">该分类暂无漫画</p>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-12">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'instant' }); }}
                      className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-all ${
                        currentPage === page
                          ? 'bg-primary-500 text-background-50'
                          : 'border border-background-200 text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}